import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';

import { GlobalFeedbackService } from '../services/global-feedback.service';
import { AuthSessionService } from '../services/auth-session.service';
import { normalizeApiError } from '../utils/error-normalizer';

const isAuthRoute = (url: string): boolean => {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/signup') ||
    url.includes('/auth/forgot-password') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout')
  );
};

const isPublicSignRoute = (url: string): boolean => {
  return url.includes('/sign/') || url.includes('/review/');
};

const isPublicDocumentConfigRoute = (url: string): boolean => {
  return url.includes('/public/document-config/');
};

const isPublicSenderIdentityApprovalRoute = (url: string): boolean => {
  return url.includes('/sender-identities/approve');
};

const withRetriedHeader = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
  return request.clone({
    headers: request.headers.set('x-auth-retry', '1'),
  });
};

const hasRetriedHeader = (request: HttpRequest<unknown>): boolean => {
  return request.headers.get('x-auth-retry') === '1';
};

export const authRefreshInterceptor: HttpInterceptorFn = (request, next) => {
  const authSession = inject(AuthSessionService);
  const feedback = inject(GlobalFeedbackService);
  const router = inject(Router);

  return next(request).pipe(
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        !isAuthRoute(request.url) &&
        !isPublicSignRoute(request.url) &&
        !isPublicDocumentConfigRoute(request.url) &&
        !isPublicSenderIdentityApprovalRoute(request.url) &&
        error.status !== 401
      ) {
        feedback.showError(normalizeApiError(error));
      }

      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      if (
        isAuthRoute(request.url) ||
        isPublicSignRoute(request.url) ||
        isPublicDocumentConfigRoute(request.url) ||
        isPublicSenderIdentityApprovalRoute(request.url)
      ) {
        return throwError(() => error);
      }

      if (hasRetriedHeader(request) || !authSession.shouldAttemptRefresh()) {
        authSession.handleUnauthorizedRedirect({
          reason: 'session_expired',
          returnUrl: router.url || '/',
        });
        return throwError(() => error);
      }

      return authSession.refreshSession().pipe(
        switchMap(() => {
          const latestToken = authSession.accessToken();
          const retriedRequest = withRetriedHeader(
            latestToken
              ? request.clone({
                  setHeaders: {
                    Authorization: `Bearer ${latestToken}`,
                  },
                })
              : request,
          );
          return next(retriedRequest);
        }),
        catchError((refreshError: unknown) => {
          authSession.handleUnauthorizedRedirect({
            reason: 'session_expired',
            returnUrl: router.url || '/',
          });
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
